import { tasksAPI } from '../api/api';

const api = tasksAPI as any;

// Helper function to check if we're in Electron
const isElectron = (): boolean => {
  return typeof window !== 'undefined' && window.electronAPI !== undefined;
};

// URL для API уведомлений (используем тот же подход, что и в api.ts)
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    // Если URL содержит /api/health, убираем его
    if (envUrl.includes('/api/health')) {
      return envUrl.replace('/api/health', '');
    }
    // Если URL заканчивается на /api, убираем /api
    if (envUrl.endsWith('/api')) {
      return envUrl.replace('/api', '');
    }
    return envUrl;
  }
  
  // Для Electron приложения используем специальный URL
  if (isElectron()) {
    return 'http://task.clowoow.ru:5000';
  }
  
  // По умолчанию используем боевой API
  return 'https://prometey-task.clowoow.ru';
};

const API_URL = getApiUrl();

// Получить токен из localStorage
const getToken = (): string | null => {
  try {
    return localStorage.getItem('token');
  } catch (error) {
    return null;
  }
};

// Регистрация service worker
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  // В Electron приложении Service Worker не поддерживается
  if (isElectron()) {
    console.log('Electron: Service Worker не поддерживается');
    return null;
  }

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker зарегистрирован:', registration);
      return registration;
    } catch (error) {
      console.error('Ошибка регистрации Service Worker:', error);
      return null;
    }
  }
  return null;
}

// Запросить разрешение на уведомления
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('Браузер не поддерживает уведомления');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('Разрешение на уведомления отклонено');
    return 'denied';
  }

  // Запрашиваем разрешение
  const permission = await Notification.requestPermission();
  return permission;
}

// Счетчик попыток подписки (чтобы избежать бесконечного цикла)
let subscriptionAttempts = 0;
const MAX_SUBSCRIPTION_ATTEMPTS = 3;

// Подписаться на push-уведомления (обязательная подписка)
export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  // В Electron приложении Web Push API не поддерживается
  if (isElectron()) {
    console.log('Electron: Web Push не поддерживается, пропускаем подписку');
    return null;
  }

  if (subscriptionAttempts >= MAX_SUBSCRIPTION_ATTEMPTS) {
    console.warn(`Достигнут лимит попыток подписки (${MAX_SUBSCRIPTION_ATTEMPTS}). Повторная попытка через 60 секунд.`);
    subscriptionAttempts = 0; // Сбрасываем счетчик через минуту
    setTimeout(() => {
      subscribeToPushNotifications().catch(err => 
        console.error('Повторная попытка подписки не удалась:', err)
      );
    }, 60000);
    return null;
  }

  subscriptionAttempts++;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Получаем публичный VAPID ключ
    const token = getToken();
    if (!token) {
      console.warn('Токен не найден, невозможно подписаться на уведомления');
      return null;
    }

    const response = await fetch(`${API_URL}/api/notifications/vapid-public-key`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Если 502 или другая ошибка сервера, не пытаемся бесконечно
      if (response.status >= 500) {
        console.error(`Ошибка сервера при получении VAPID ключа: ${response.status}. Проверьте, что сервер запущен и пересобран.`);
        return null;
      }
      throw new Error(`Не удалось получить VAPID ключ: ${response.status}`);
    }

    const { publicKey } = await response.json();

    // Проверяем существующую подписку
    let subscription = await registration.pushManager.getSubscription();
    
    // Если подписки нет или она недействительна, создаем новую
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    // Всегда отправляем подписку на сервер (принудительно обновляем)
    const subscribeResponse = await fetch(`${API_URL}/api/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        subscription: {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')!),
            auth: arrayBufferToBase64(subscription.getKey('auth')!),
          },
        },
      }),
    });

    if (!subscribeResponse.ok) {
      throw new Error(`Не удалось сохранить подписку на сервере: ${subscribeResponse.status}`);
    }

    console.log('Подписка на push-уведомления успешно активирована (обязательна)');
    subscriptionAttempts = 0; // Сбрасываем счетчик при успехе
    return subscription;
  } catch (error: any) {
    console.error('Ошибка подписки на push-уведомления:', error);
    
    // Если это ошибка сети или сервера, не пытаемся бесконечно
    if (error.message?.includes('502') || error.message?.includes('Bad Gateway')) {
      console.error('Сервер недоступен. Убедитесь, что сервер запущен и пересобран после добавления роутов уведомлений.');
      return null;
    }
    
    // Пытаемся повторно подписаться только если не достигнут лимит
    if (subscriptionAttempts < MAX_SUBSCRIPTION_ATTEMPTS) {
      setTimeout(() => {
        subscribeToPushNotifications().catch(err => 
          console.error('Повторная попытка подписки не удалась:', err)
        );
      }, 5000); // Увеличиваем задержку до 5 секунд
    }
    return null;
  }
}

// Счетчик попыток инициализации
let initAttempts = 0;
const MAX_INIT_ATTEMPTS = 3;
let checkInterval: NodeJS.Timeout | null = null;

// Показать уведомление (работает и в браузере, и в Electron)
export function showNotification(title: string, body: string, data?: any): void {
  if (isElectron() && window.electronAPI?.showNotification) {
    // Используем нативные уведомления Electron через IPC
    window.electronAPI.showNotification(title, body, data);
  } else if ('Notification' in window && Notification.permission === 'granted') {
    // Используем стандартные браузерные уведомления
    const notification = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: data?.tag || 'task-notification',
      data: data || {},
    });

    notification.onclick = () => {
      window.focus();
      if (data?.url) {
        window.location.href = data.url;
      }
      notification.close();
    };
  }
}

// Инициализация уведомлений (вызывается при входе пользователя)
// Подписка обязательна и принудительно активируется при каждом входе
export async function initializeNotifications(): Promise<void> {
  // В Electron приложении Web Push API не поддерживается
  // Используем нативные уведомления Electron вместо этого
  if (isElectron()) {
    console.log('Electron приложение: Web Push не поддерживается, используются нативные уведомления');
    // Запрашиваем разрешение на нативные уведомления
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
    // В Electron уведомления будут приходить через polling или WebSocket
    // и показываться через showNotification()
    return;
  }

  if (initAttempts >= MAX_INIT_ATTEMPTS) {
    console.warn(`Достигнут лимит попыток инициализации (${MAX_INIT_ATTEMPTS}). Повторная попытка через 60 секунд.`);
    initAttempts = 0;
    setTimeout(() => initializeNotifications(), 60000);
    return;
  }

  initAttempts++;
  
  try {
    // Регистрируем service worker
    const registration = await registerServiceWorker();
    if (!registration) {
      console.warn('Service Worker не зарегистрирован, повторная попытка через 5 секунд...');
      setTimeout(() => initializeNotifications(), 5000);
      return;
    }

    // Запрашиваем разрешение (если еще не получено)
    const permission = await requestNotificationPermission();
    if (permission === 'denied') {
      console.warn('Разрешение на уведомления отклонено пользователем. Уведомления критичны для работы системы.');
      // Пытаемся повторно запросить через большее время
      setTimeout(() => initializeNotifications(), 30000);
      return;
    }

    if (permission !== 'granted') {
      console.warn('Разрешение на уведомления не получено, повторная попытка через 10 секунд...');
      setTimeout(() => initializeNotifications(), 10000);
      return;
    }

    // Принудительно подписываемся на push-уведомления (обязательно)
    await subscribeToPushNotifications();
    
    initAttempts = 0; // Сбрасываем счетчик при успехе
    
    // Проверяем подписку периодически и переподписываемся при необходимости
    // Очищаем предыдущий интервал, если он был
    if (checkInterval) {
      clearInterval(checkInterval);
    }
    
    checkInterval = setInterval(async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) {
          console.log('Подписка отсутствует, принудительно переподписываемся...');
          subscriptionAttempts = 0; // Сбрасываем счетчик перед новой попыткой
          await subscribeToPushNotifications();
        }
      } catch (err) {
        console.error('Ошибка проверки подписки:', err);
      }
    }, 60000); // Проверяем каждую минуту
  } catch (error) {
    console.error('Ошибка инициализации уведомлений:', error);
    // Повторная попытка через 10 секунд, если не достигнут лимит
    if (initAttempts < MAX_INIT_ATTEMPTS) {
      setTimeout(() => initializeNotifications(), 10000);
    }
  }
}

// Вспомогательные функции
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

